<template>
  <div class="report-page">
    <!-- Header -->
    <div class="report-header d-flex align-center justify-space-between px-3 px-sm-6 py-3">
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn icon @click="$router.push({ name: 'auth', params: { authPage: 'shop' } })">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h2 class="mb-0 primary--text font-weight-bold" style="font-size: 20px;">
          <v-icon color="primary" class="mr-1" style="vertical-align: middle;">mdi-bullhorn-outline</v-icon>
          Ads Report
        </h2>
      </div>
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn depressed color="primary" :loading="isLoading" @click="fetchAdsReport">
          <v-icon small left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <span class="grey--text text--darken-1 d-none d-sm-inline" style="font-size: 13px;"
          >Ads Performance Dashboard</span
        >
      </div>
    </div>

    <v-divider />

    <!-- Body -->
    <div class="report-body pa-3 pa-sm-5">
      <!-- ===== Date Filter Section ===== -->
      <v-card rounded="lg" class="pa-4 mb-4 date-filter-card" elevation="1">
        <div class="d-flex align-center justify-space-between" style="gap: 12px; flex-wrap: wrap;">
          <div class="d-flex align-center" style="gap: 8px;">
            <v-icon color="primary">mdi-calendar</v-icon>
            <span class="font-weight-bold primary--text" style="font-size: 15px;">Report Date</span>
          </div>
          <div class="d-flex align-center" style="gap: 8px; flex-wrap: wrap;">
            <v-btn small outlined color="primary" style="text-transform: none;" @click="goPrevRange">
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  outlined
                  color="primary"
                  style="min-width: 260px; text-transform: none;"
                >
                  <v-icon small left>mdi-calendar-range</v-icon>
                  {{ formatRangeDisplay(startDate, endDate) }}
                </v-btn>
              </template>
              <v-date-picker v-model="dateRange" no-title scrollable range :max="today" @input="onDateRangeInput" />
            </v-menu>
            <v-btn
              small
              outlined
              color="primary"
              style="text-transform: none;"
              :disabled="endDate >= today"
              @click="goNextRange"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn small text color="primary" style="text-transform: none;" @click="goToday">
              <v-icon small left>mdi-calendar-today-outline</v-icon>
              Today
            </v-btn>
            <v-btn depressed color="primary" :loading="isLoading" @click="fetchAdsReport">
              <v-icon small left>mdi-refresh</v-icon>
              Apply
            </v-btn>
          </div>
        </div>
      </v-card>

      <!-- ===== Summary Metrics Section ===== -->
      <v-card rounded="lg" class="pa-4 mb-5" elevation="1">
        <h3 class="primary--text font-weight-bold mb-4 d-flex align-center" style="font-size: 15px;">
          <v-icon color="primary" small class="mr-1">mdi-chart-line</v-icon>
          Overall Summary
        </h3>

        <!-- Metric grid -->
        <div class="metric-grid">
          <!-- Revenue Card -->
          <div class="metric-card metric-rev">
            <div class="metric-label">Revenue</div>
            <div class="metric-value green--text text--darken-2">
              {{ isLoading ? '—' : formatCurrency(summary.rev) }}
            </div>
          </div>

          <!-- Spent Card -->
          <div class="metric-card metric-spent">
            <div class="metric-label">Ad Spent</div>
            <div class="metric-value red--text text--darken-1">
              {{ isLoading ? '—' : formatCurrency(summary.spent) }}
            </div>
          </div>

          <!-- Orders Card -->
          <div class="metric-card metric-sales">
            <div class="metric-label">Orders</div>
            <div class="metric-value blue--text text--darken-2">
              {{ isLoading ? '—' : (summary.order || 0).toLocaleString() }}
            </div>
          </div>

          <!-- View Content Card -->
          <div class="metric-card metric-view-content">
            <div class="metric-label">View Content</div>
            <div class="metric-value indigo--text text--darken-2">
              {{ isLoading ? '—' : (summary.view_content || 0).toLocaleString() }}
            </div>
          </div>

          <!-- Add to Cart Card -->
          <div class="metric-card metric-add-to-cart">
            <div class="metric-label">Add to Cart</div>
            <div class="metric-value purple--text text--darken-2">
              {{ isLoading ? '—' : (summary.add_to_cart || 0).toLocaleString() }}
            </div>
          </div>
        </div>
      </v-card>

      <!-- ===== Detailed Table Section ===== -->
      <v-card rounded="lg" class="pa-4" elevation="1">
        <div class="d-flex align-center justify-space-between mb-4 flex-wrap" style="gap: 12px;">
          <h3 class="primary--text font-weight-bold mb-0 d-flex align-center" style="font-size: 15px;">
            <v-icon color="primary" small class="mr-1">mdi-table</v-icon>
            Detailed Metrics
            <v-chip v-if="filteredItems.length" x-small label color="primary" outlined class="ml-2 font-weight-bold">
              {{ filteredItems.length }} Ads
            </v-chip>
          </h3>
          <!-- Client-side filter search -->
          <div style="max-width: 380px; width: 100%;">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search Campaign or Ad ID"
              dense
              outlined
              hide-details
              clearable
            />
          </div>
        </div>

        <div class="table-scroll-wrapper">
          <v-data-table
            :headers="tableHeaders"
            :items="filteredItems"
            :loading="isLoading"
            :items-per-page="20"
            class="report-table"
          >
            <!-- UTM Campaign & Ad ID template -->
            <template v-slot:[`item.utm`]="{ item }">
              <div class="py-1">
                <div>Ad ID: {{ item.adId || '—' }}</div>
                <div
                  style="max-width: 320px; display: -webkit-box; -webkit-line-clamp: 2; line-clamp:2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; word-break: break-word;"
                  :title="item.campaign || '—'"
                >
                  Campaign: {{ item.campaign || '—' }}
                </div>
              </div>
            </template>

            <!-- View Content template -->
            <template v-slot:[`item.view_content`]="{ item }">
              <span class="font-weight-medium">{{ (item.view_content || 0).toLocaleString() }}</span>
            </template>

            <!-- Add to Cart template -->
            <template v-slot:[`item.add_to_cart`]="{ item }">
              <span class="font-weight-medium">{{ (item.add_to_cart || 0).toLocaleString() }}</span>
            </template>

            <!-- Reached Checkout template -->
            <template v-slot:[`item.reached_checkout`]="{ item }">
              <span class="font-weight-medium">{{ (item.reached_checkout || 0).toLocaleString() }}</span>
            </template>

            <!-- Orders template -->
            <template v-slot:[`item.orders`]="{ item }">
              <span class="font-weight-bold blue--text text--darken-2">{{ (item.orders || 0).toLocaleString() }}</span>
            </template>

            <!-- Revenue template -->
            <template v-slot:[`item.revenue`]="{ item }">
              <span class="font-weight-bold green--text text--darken-2">{{ formatCurrency(item.revenue) }}</span>
            </template>

            <!-- Spent template -->
            <template v-slot:[`item.spent`]="{ item }">
              <span class="font-weight-bold red--text text--darken-1">{{ formatCurrency(item.spent) }}</span>
            </template>

            <!-- Empty state -->
            <template v-slot:no-data>
              <div class="text-center py-8 grey--text">
                <v-icon large color="grey lighten-2">mdi-chart-bar-off</v-icon>
                <div class="mt-2" style="font-size: 13px;">No ads data available</div>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import { reportApi } from '@/apis/report';

export default {
  name: 'ReportAds',
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      today,
      startDate: today,
      endDate: today,
      dateRange: [today, today],
      dateMenu: false,
      isLoading: false,
      searchQuery: '',
      summary: {
        order: 0,
        rev: 0,
        spent: 0,
        view_content: 0,
        add_to_cart: 0,
      },
      items: [],
      tableHeaders: [
        { text: 'UTM (Ad ID / Campaign)', value: 'utm', sortable: false },
        { text: 'Revenue', value: 'revenue', sortable: true, align: 'right' },
        { text: 'Spent', value: 'spent', sortable: true, align: 'right' },
        { text: 'View Content', value: 'view_content', sortable: true, align: 'right' },
        { text: 'Add to Cart', value: 'add_to_cart', sortable: true, align: 'right' },
        { text: 'Reached Checkout', value: 'reached_checkout', sortable: true, align: 'right' },
        { text: 'Orders', value: 'orders', sortable: true, align: 'right' },
      ],
    };
  },
  computed: {
    filteredItems() {
      if (!this.searchQuery) return this.items;
      const query = this.searchQuery.toLowerCase().trim();
      return this.items.filter(item => {
        const campaign = (item.campaign || '').toLowerCase();
        const adId = (item.adId || '').toLowerCase();
        return campaign.includes(query) || adId.includes(query);
      });
    },
  },
  created() {
    this.fetchAdsReport();
  },
  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined) return '$0.00';
      return '$' + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    },
    formatRangeDisplay(start, end) {
      if (!start && !end) return '';
      if (!end || start === end) return this.formatDisplayDate(start);
      const fmtShort = s => {
        const d = new Date(s + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      };
      return `${fmtShort(start)} → ${fmtShort(end)}`;
    },
    toDateString(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    rangeDays() {
      const s = new Date(this.startDate + 'T00:00:00');
      const e = new Date(this.endDate + 'T00:00:00');
      const diff = Math.round((e - s) / 86400000);
      return Math.max(0, diff) + 1;
    },
    onDateRangeInput(val) {
      if (!Array.isArray(val) || val.length < 2) return;
      const sorted = [...val].sort();
      this.startDate = sorted[0];
      this.endDate = sorted[1];
      this.dateRange = sorted;
      this.dateMenu = false;
      this.fetchAdsReport();
    },
    shiftRange(direction) {
      const days = this.rangeDays();
      const start = new Date(this.startDate + 'T00:00:00');
      const end = new Date(this.endDate + 'T00:00:00');
      start.setDate(start.getDate() + direction * days);
      end.setDate(end.getDate() + direction * days);
      let nextStart = this.toDateString(start);
      let nextEnd = this.toDateString(end);
      if (nextEnd > this.today) {
        const overflow = Math.round((new Date(nextEnd + 'T00:00:00') - new Date(this.today + 'T00:00:00')) / 86400000);
        const adjStart = new Date(nextStart + 'T00:00:00');
        adjStart.setDate(adjStart.getDate() - overflow);
        nextStart = this.toDateString(adjStart);
        nextEnd = this.today;
      }
      this.startDate = nextStart;
      this.endDate = nextEnd;
      this.dateRange = [nextStart, nextEnd];
      this.fetchAdsReport();
    },
    goPrevRange() {
      this.shiftRange(-1);
    },
    goNextRange() {
      if (this.endDate >= this.today) return;
      this.shiftRange(1);
    },
    goToday() {
      if (this.startDate === this.today && this.endDate === this.today) return;
      this.startDate = this.today;
      this.endDate = this.today;
      this.dateRange = [this.today, this.today];
      this.fetchAdsReport();
    },
    async fetchAdsReport() {
      this.isLoading = true;
      try {
        const params = {
          from: this.startDate,
          to: this.endDate,
        };
        const { data } = await reportApi.getReportAdsBySeller(params);
        if (data) {
          this.summary = {
            order: data.summary?.order || 0,
            rev: data.summary?.rev || 0,
            spent: data.summary?.spent || 0,
            view_content: data.summary?.view_content || 0,
            add_to_cart: data.summary?.add_to_cart || 0,
          };
          this.items = Array.isArray(data.data) ? data.data : [];
        }
      } catch (error) {
        console.error('fetchAdsReport error:', error);
        this.$store?.commit?.('setMessages', {
          messages: error.response?.data?.message || 'Failed to load ads report',
          type: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.report-page {
  min-height: 100vh;
  background: #f4f6fb;
  display: flex;
  flex-direction: column;
}

.report-header {
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 10;
}

.report-body {
  flex: 1;
}

/* Metric cards container — responsive grid */
.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

/* Metric cards */
.metric-card {
  border-left: 4px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  padding: 12px 16px;

  .metric-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #888;
    margin-bottom: 4px;
  }
  .metric-value {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
  }
}

.metric-rev {
  border-color: #2e7d32;
  background: #f1f8f1;
}
.metric-spent {
  border-color: #c62828;
  background: #fef2f2;
}
.metric-sales {
  border-color: #1565c0;
  background: #f0f4ff;
}
.metric-view-content {
  border-color: #3f51b5;
  background: #f5f7ff;
}
.metric-add-to-cart {
  border-color: #e040fb;
  background: #fdf2ff;
}

/* Table */
.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
}

.report-table {
  border-radius: 8px;

  ::v-deep thead th {
    background: #f0f4ff !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #3f51b5 !important;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
  }

  ::v-deep tbody td {
    padding-top: 14px !important;
    padding-bottom: 14px !important;
    font-size: 14px !important;
  }

  ::v-deep tbody tr:hover {
    background: #f8f9ff !important;
  }
}

@media (max-width: 599px) {
  .metric-card {
    padding: 10px 12px;

    .metric-value {
      font-size: 18px;
    }
  }

  .report-table {
    ::v-deep tbody td,
    ::v-deep thead th {
      white-space: nowrap;
      padding-left: 8px !important;
      padding-right: 8px !important;
      font-size: 13px !important;
    }
  }

  .report-header {
    h2 {
      font-size: 15px !important;
    }
  }

  .report-body ::v-deep .v-card.pa-4 {
    padding: 12px !important;
  }
}
</style>
